/**
 * src/api/assetApi.js
 *
 * Thin client for the assets-write Azure Function — the dedicated per-record
 * write path for the asset register and Business Impact Analysis (BIA)
 * (ADR-0005, docs/decisions/0005-assets-bia-per-record-storage.md). Mirrors
 * vendorApi.js's / controlsApi.js's shape: one wrapper per action, each
 * returning the function's response body so callers can reconcile local
 * state without a full reload.
 */

import { azurePost } from './azureClient';

async function callAssetsWrite(action, body, tenantId) {
  return azurePost(`/api/assets-write?action=${encodeURIComponent(action)}`, {
    ...body,
    tenantId,
  });
}

/** Create-or-update an asset by asset.id. Returns { ok, asset }. */
export async function saveAssetRecord(asset, tenantId) {
  return callAssetsWrite('save-asset', { asset }, tenantId);
}

/** Delete one asset by id. Returns { ok }. */
export async function deleteAssetRecord(assetId, tenantId) {
  return callAssetsWrite('delete-asset', { assetId }, tenantId);
}

/** Create-or-update a BIA assessment by bia.id. Returns { ok, bia }. */
export async function saveBiaRecord(bia, tenantId) {
  return callAssetsWrite('save-bia', { bia }, tenantId);
}

/** Delete one BIA assessment by id. Returns { ok }. */
export async function deleteBiaRecord(biaId, tenantId) {
  return callAssetsWrite('delete-bia', { biaId }, tenantId);
}

/**
 * GH #7191 (BIA-B-002): an administrator's attributed manual criticality
 * override — { assetId, criticality, reason } — or its revert to the BIA
 * value — { assetId, revert: true, reason }. Returns { ok, asset,
 * criticalityConflicts }.
 */
export async function overrideAssetCriticality(body, tenantId) {
  return callAssetsWrite('override-criticality', body, tenantId);
}

/** Patch an allow-listed set of fields across multiple assets. Returns { ok, updatedCount }. */
export async function bulkUpdateAssets(ids, updates, tenantId) {
  return callAssetsWrite('bulk-update', { ids, updates }, tenantId);
}
